import { Visibility, VisibilityOff } from '@mui/icons-material';
import clsx from 'clsx';
import React, {
  type FC,
  forwardRef,
  type InputHTMLAttributes,
  useState,
} from 'react';
import { type FieldError } from 'react-hook-form';

import styles from './Input.module.scss';

interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  srLabel?: boolean;
  error?: FieldError | string;
  prefix?: React.ReactNode | string;
  suffix?: React.ReactNode | string;
  helperText?: React.ReactNode | string;
  className?: string;
}

const Input: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      type = 'text',
      required = false,
      onChange,
      srLabel = false,
      error,
      placeholder = '',
      prefix,
      disabled = false,
      suffix,
      helperText,
      className,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const toggleVisibility = () => setShowPassword((v) => !v);

    return (
      <div className={clsx(styles.base, className)} data-ui="input">
        <label>
          <span
            className={clsx({
              'sr-only': srLabel,
              [styles.required]: required,
            })}
            data-ui="input-label"
          >
            {label}
          </span>
          <div className={styles.wrapper}>
            {prefix && (
              <div className={styles.prefix} data-ui="input-prefix">
                {prefix}
              </div>
            )}
            <input
              disabled={disabled}
              onChange={onChange}
              placeholder={placeholder}
              ref={ref}
              type={inputType}
              {...props}
            />
            {isPassword && !suffix && (
              <button
                className={styles.visibilityToggle}
                onClick={toggleVisibility}
                type="button"
              >
                <span className="sr-only">
                  {showPassword ? 'Hide' : 'Show'}
                </span>
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </button>
            )}
            {suffix && (
              <div className={styles.suffix} data-ui="input-suffix">
                {suffix}
              </div>
            )}
          </div>
        </label>
        {helperText && <p className={styles.helper}>{helperText}</p>}
        {error && (
          <p className={styles.error}>
            {typeof error === 'object' ? error.message : error}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
